import { designTokens } from '@monorepo/nova-ui';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AlignmentStatus } from '../types';

/**
 * Main card wrapper with border and padding
 */
export const StyledCard = styled(Stack)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.customShape.borderRadiusXLarge,
  padding: `${theme.spacing(4)} ${theme.spacing(6)}`,
  gap: theme.spacing(4),
  containerType: 'inline-size',
  overflow: 'hidden',

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
  },
}));

/**
 * Header container - flexbox for title, info icon, and sample count tag
 */
export const StyledHeaderContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

/**
 * Title container with icon
 */
export const StyledTitleContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

/**
 * Title text styling
 */
export const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.08px',
  color: theme.palette.text.primary,
}));

/**
 * Sample count tag (pill badge)
 */
export const StyledSampleTag = styled(Chip)(({ theme }) => ({
  height: theme.spacing(6),
  padding: theme.spacing(1, 3),
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.5%',
  backgroundColor: theme.palette.background.contrast,
  border: `1px solid ${theme.palette.text.fifth}`,
  color: theme.palette.text.secondary,
  borderRadius: theme.customShape.borderRadiusFull,

  '& .MuiChip-label': {
    padding: 0,
  },
}));

/**
 * Subtitle text styling
 */
export const StyledSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(20),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.03px',
  color: theme.palette.text.secondary,

  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.pxToRem(16),
  },
}));

/**
 * Content row container - factor cards and gauge side by side
 */
export const StyledContentRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(4),
  width: '100%',

  '@container (max-width: 900px)': {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
}));

/**
 * Factor cards container
 */
export const StyledFactorCardsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(4),
  flex: '3 1 0%',

  '@container (max-width: 900px)': {
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(1.5),
  },
}));

/**
 * Individual factor card
 */
export const StyledFactorCard = styled(Stack)(({ theme }) => ({
  flex: '1 0 auto',
  justifyContent: 'space-between',
  padding: '25px 26px',
  backgroundColor: theme.palette.background.contrast,
  border: `1px solid ${theme.palette.text.fifth}`,
  borderRadius: theme.customShape.borderRadiusXLarge,
  overflow: 'hidden',
  isolation: 'isolate',
  minWidth: 0,
  minHeight: theme.spacing(19.25),

  '@container (max-width: 600px)': {
    padding: theme.spacing(7.25, 8.25),
    minHeight: theme.spacing(16),
  },
}));

/**
 * Icon circle container
 */
export const StyledIconCircle = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2.5),
  width: theme.spacing(12.5),
  height: theme.spacing(12.5),
  borderRadius: theme.customShape.borderRadiusFull,
  border: `1px solid ${theme.palette.primary.main}`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.primary.main,
  flexShrink: 0,
}));

/**
 * Factor card inner content wrapper (icon + label group)
 */
export const StyledFactorContent = styled(Stack)(({ theme }) => ({
  alignSelf: 'stretch',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
}));

/**
 * Label + status group (tightly grouped)
 */
export const StyledFactorLabelGroup = styled(Stack)(() => ({
  gap: '1px',
}));

/**
 * Factor card label
 */
export const StyledFactorLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.08px',
  color: theme.palette.text.secondary,

  '@container (max-width: 600px)': {
    fontSize: theme.typography.pxToRem(13),
  },
}));

/**
 * Status container (dot + text)
 */
export const StyledStatusContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.25),
}));

/**
 * Status dot (colored circle)
 */
interface StatusDotProps {
  status: AlignmentStatus;
}

export const StyledStatusDot = styled(Box)<StatusDotProps>(({
  theme,
  status,
}) => {
  let color: string;
  switch (status) {
    case AlignmentStatus.ALIGNED:
      color = theme.palette.success.main;
      break;
    case AlignmentStatus.FRAGMENTED:
      color = theme.palette.error.main;
      break;
    case AlignmentStatus.MIXED:
      color = theme.palette.warning.main;
      break;
    default:
      color = theme.palette.text.secondary;
  }

  return {
    width: '13px',
    height: '13px',
    borderRadius: '50%',
    backgroundColor: color,
  };
});

/**
 * Status text (Aligned/Fragmented/Mixed)
 */
export const StyledStatusText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.08px',
  color: theme.palette.text.primary,

  '@container (max-width: 600px)': {
    fontSize: theme.typography.pxToRem(13),
  },
}));

/**
 * Gauge section wrapper - alignment label left, gauge visualization right
 */
export const StyledGaugeSection = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  flex: '2 0 0%',
  maxWidth: '460px',

  '@container (max-width: 900px)': {
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 'none',
  },
}));

/**
 * Gauge container wrapper
 */
export const StyledGaugeContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  flex: 1,
  marginTop: theme.spacing(-4),
  marginLeft: theme.spacing(-24),

  '@container (max-width: 900px)': {
    width: '100%',
    alignItems: 'center',
    marginLeft: 0,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

/**
 * Alignment label above gauge
 */
export const StyledAlignmentLabel = styled(Typography)(({ theme }) => ({
  fontSize: '20px !important',
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.03px',
  color: theme.palette.text.secondary,
  maxWidth: 160,
  flexShrink: 0,

  '@container (max-width: 900px)': {
    marginTop: theme.spacing(5),
    textAlign: 'center',
    alignSelf: 'center',
    maxWidth: 'none',
  },
}));

/**
 * SVG gauge wrapper
 */
export const StyledGaugeSvg = styled('svg')(() => ({
  display: 'block',
  width: '100%',
  height: 'auto',
  maxWidth: 280,
}));

/**
 * Score value display (large number)
 */
export const StyledScoreValue = styled(Typography)(({ theme }) => ({
  fontSize: '36px !important',
  fontWeight: 400,
  fontStyle: 'italic',
  lineHeight: 1,
  letterSpacing: '0.09px',
  color: theme.palette.text.primary,
  textAlign: 'center',
  marginTop: theme.spacing(-14),
}));

/**
 * Score description text below score
 */
export const StyledScoreDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  maxWidth: 150,
}));

/**
 * Gauge labels container (Fragmented / Aligned + description)
 */
export const StyledGaugeLabelsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
  maxWidth: 310,
  marginLeft: theme.spacing(-4),

  '@container (max-width: 600px)': {
    marginLeft: 0,
    marginTop: theme.spacing(2),
  },
}));

/**
 * Gauge label text (Fragmented / Aligned)
 */
export const StyledGaugeLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.08px',
  color: theme.palette.text.primary,
}));

/**
 * Divider line
 */
export const StyledDivider = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '1px',
  backgroundColor: theme.palette.divider,
}));

/**
 * Expandable content section
 */
export const StyledExpandableContent = styled(Collapse)(() => ({
  width: '100%',
}));

/**
 * Toggle button (link-styled) for expanding/collapsing
 */
interface ToggleButtonProps {
  isExpanded: boolean;
}

export const StyledToggleButton = styled(Button)<ToggleButtonProps>(
  ({ theme, isExpanded }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: 0,
    minWidth: 0,
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.07px',
    textTransform: 'none',
    borderRadius: theme.spacing(3.75),
    backgroundColor: 'transparent',

    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },

    '& .MuiButton-startIcon': {
      fontSize: theme.typography.pxToRem(16),
    },

    '& .MuiSvgIcon-root': {
      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 0.3s ease',
    },

    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(13),
      marginBottom: theme.spacing(4),
    },
  }),
);

/**
 * Educational content panel wrapper
 */
interface EducationalPanelProps {
  isExpanded: boolean;
}

export const StyledEducationalPanel = styled(Box)<EducationalPanelProps>(
  ({ theme, isExpanded }) => ({
    backgroundColor: isExpanded
      ? theme.palette.mode === 'dark'
        ? designTokens.colors.blue[1100]
        : designTokens.colors.blue[1000]
      : 'transparent',
    borderRadius: theme.customShape.borderRadiusXLarge,
    padding: `${theme.spacing(2.25)} !important`,
    paddingTop: theme.spacing(4),
    transition: theme.transitions.create(['background-color', 'padding'], {
      duration: theme.transitions.duration.standard,
    }),

    [theme.breakpoints.down('sm')]: {
      padding: isExpanded ? theme.spacing(2) : 1,
    },
  }),
);

/**
 * Educational content header with close button
 */
export const StyledEducationalHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  height: '32px',
  padding: theme.spacing(0, 2),
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: '1 0 0',
  position: 'relative',

  '& .MuiButton-root': {
    whiteSpace: 'normal',
    textAlign: 'center',
  },

  [theme.breakpoints.down(600)]: {
    height: 'auto',
    marginTop: theme.spacing(2),
    flexWrap: 'nowrap',

    '& .MuiButton-root': {
      marginBottom: 0,
      whiteSpace: 'normal',
      textAlign: 'center',
      alignItems: 'flex-start',

      '& .MuiButton-startIcon': {
        marginTop: theme.spacing(0.5),
        marginRight: 0,
        marginLeft: 0,
      },
    },
  },
}));

/**
 * Educational content section title
 */
export const StyledEducationalTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 600,
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

/**
 * Educational content body text
 */
export const StyledEducationalText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
}));

/**
 * Educational content bullet list
 */
export const StyledBulletList = styled('ul')(({ theme }) => ({
  margin: theme.spacing(1, 0, 0, 2),
  padding: 0,
  paddingLeft: theme.spacing(4),
  listStyleType: 'disc',

  '& li': {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
}));

/**
 * Educational content quote/bottom line
 */
export const StyledQuote = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(32),
  fontWeight: 400,
  fontStyle: 'italic',
  lineHeight: 1.6,
  color: theme.palette.text.secondary,
}));

/**
 * Close button for educational section
 */
export const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  flexShrink: 0,
  position: 'absolute',
  right: theme.spacing(2),

  '& svg': {
    width: '14.17px',
    height: '14.17px',
  },

  [theme.breakpoints.down(600)]: {
    position: 'static',
    marginLeft: 'auto',
  },
}));

/**
 * Educational content stack with themed gap
 * Matches RR pattern
 */
export const StyledEducationalContentStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(4.5),
  padding: '5px',
}));

/**
 * Icon and label column wrapper inside a factor card
 */
export const StyledFactorIconGroup = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
}));
